const getControls = (form, fieldName) =>
    Array.from(form.elements).filter((control) => control.name === fieldName);

const updateValidationState = (message, controls) => {
    const hasError = message.textContent.trim().length > 0;

    controls.forEach((control) => {
        control.setAttribute('aria-invalid', hasError.toString());
    });
};

const connectValidationMessage = (message, index) => {
    const form = message.closest('form');
    const fieldName = message.dataset.valmsgFor;

    if (!form || !fieldName) {
        return;
    }

    const controls = getControls(form, fieldName);

    if (controls.length === 0) {
        return;
    }

    message.id ||= `${controls[0].id || `validation-field-${index}`}-error`;
    message.setAttribute('aria-live', 'polite');

    controls.forEach((control) => {
        const describedBy = new Set((control.getAttribute('aria-describedby') || '').split(' ').filter(Boolean));
        describedBy.add(message.id);
        control.setAttribute('aria-describedby', Array.from(describedBy).join(' '));
    });

    updateValidationState(message, controls);

    new MutationObserver(() => updateValidationState(message, controls)).observe(message, {
        childList: true,
        characterData: true,
        subtree: true
    });
};

const connectMarketingConsentLabel = (control, index) => {
    if (control.labels?.length || control.hasAttribute('aria-label') || control.hasAttribute('aria-labelledby')) {
        return;
    }

    const description = control.closest('.relative.flex.items-start')?.querySelector('div[x-data]');

    if (!description) {
        return;
    }

    description.id ||= `${control.id || `marketing-consent-${index}`}-description`;
    control.setAttribute('aria-labelledby', description.id);
};

const enhanceContentTable = (table, index) => {
    const firstRow = table.rows[0];

    if (firstRow && !table.querySelector('th, [role="columnheader"]')) {
        Array.from(firstRow.cells).forEach((cell) => cell.setAttribute('role', 'columnheader'));
    }

    if (table.caption || table.hasAttribute('aria-label') || table.hasAttribute('aria-labelledby')) {
        return;
    }

    const content = table.closest('.prose');
    const precedingHeading = content
        ? Array.from(content.querySelectorAll('h1, h2, h3, h4, h5, h6'))
            .filter((heading) => heading.compareDocumentPosition(table) & Node.DOCUMENT_POSITION_FOLLOWING)
            .at(-1)
        : null;
    const sectionHeading = content?.parentElement?.querySelector(':scope > h1, :scope > h2, :scope > h3, :scope > h4, :scope > h5, :scope > h6');
    const heading = precedingHeading || sectionHeading;

    if (heading) {
        heading.id ||= `content-table-heading-${index}`;
        table.setAttribute('aria-labelledby', heading.id);
    }
};

export default function accessibilityValidation() {
    document.querySelectorAll('[data-valmsg-for]').forEach(connectValidationMessage);

    document.querySelectorAll('[data-valmsg-summary="true"]').forEach((summary) => {
        summary.setAttribute('role', 'alert');
        summary.setAttribute('aria-live', 'assertive');
    });

    document.querySelectorAll('input[name="MarketingConsent"]').forEach(connectMarketingConsentLabel);
    document.querySelectorAll('.prose table:not([role="presentation"])').forEach(enhanceContentTable);

    document.querySelectorAll('.kid span[x-on\\:click]').forEach((target) => {
        target.setAttribute('role', 'button');
        target.setAttribute('tabindex', '0');
        target.addEventListener('keydown', (event) => {
            if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                target.click();
            }
        });
    });
}
